import { useState, useCallback, useEffect } from 'react';
import {
  useGetRoadmapDetail,
  useSubscribeRoadmap,
  useUnsubscribeRoadmap,
  useDeleteRoadmap,
  type GetRoadmapDetail200,
} from '@edusense/roadmap-client';
import type { CourseSummary, UserSummary } from '@edusense/roadmap-client';
import { Course } from '../../../types/course';

// 프론트엔드용 로드맵 상세 타입 정의 (강타입 체크)
interface RoadmapDetail {
  summary: {
    id: string;
    title: string;
    description?: string | null;
    creator?: UserSummary | null;
    courseCount?: string | null;
    isSubscribed?: boolean | null;
    subscriptionCount?: number;
    createdAt?: string;
  };
  courses: Course[];
  content?: string | null;
}

/**
 * API 응답 데이터를 프론트엔드 타입으로 변환하는 유틸리티 함수
 */
const transformApiToFrontend = (apiData: any): RoadmapDetail | null => {
  if (!apiData?.summary) return null;

  const processedCourses = apiData.courses?.map((courseSummary: CourseSummary, index: number): Course => {
    const isFree = courseSummary.price === undefined || 
                  courseSummary.price === null || 
                  Number(courseSummary.price?.amount ?? 0) === 0;
    
    let progressRateNumber: number | undefined = undefined;
    if (courseSummary.progressRate !== null && courseSummary.progressRate !== undefined) {
      const parsedRate = parseFloat(courseSummary.progressRate);
      progressRateNumber = isNaN(parsedRate) ? undefined : parsedRate;
    }
    
    const isCompleted = progressRateNumber !== undefined && progressRateNumber >= 1.0;

    return {
      ...courseSummary,
      id: courseSummary.id!,
      title: courseSummary.title!,
      description: courseSummary.description,
      instructor: courseSummary.instructor,
      level: courseSummary.level!,
      tags: courseSummary.tags,
      mediaFiles: courseSummary.mediaFiles,
      price: courseSummary.price,
      averageRating: courseSummary.averageRating,
      reviewCount: courseSummary.reviewCount,
      enrollmentCount: courseSummary.enrollmentCount,
      createdAt: courseSummary.createdAt,
      isFree: isFree,
      isCompleted: isCompleted,
      progressRate: progressRateNumber,
      order: index + 1,
    };
  }) || [];

  return {
    summary: {
      ...apiData.summary,
      subscriptionCount: typeof apiData.summary.subscriptionCount === 'string'
        ? parseInt(apiData.summary.subscriptionCount, 10)
        : apiData.summary.subscriptionCount,
    },
    courses: processedCourses,
    content: apiData.content,
  };
};

/**
 * 로드맵 상세 정보를 관리하는 React Query 기반 커스텀 훅
 * @param roadmapId - 조회할 로드맵 ID (string - Long 타입 지원)
 * @returns 로드맵 데이터와 관련 액션들
 */
export const useRoadmapDetail = (roadmapId: string | undefined) => {
  const hasValidRoadmapId = roadmapId != null && roadmapId !== '';

  // Orval 생성 훅 사용
  const {
    data: roadmapData,
    isLoading,
    error: queryError,
    refetch
  } = useGetRoadmapDetail(roadmapId || '', {
    query: {
      enabled: hasValidRoadmapId,
    }
  });

  // 변환된 로드맵 데이터
  const [roadmap, setRoadmap] = useState<RoadmapDetail | null>(null);

  // API 데이터를 프론트엔드 타입으로 변환
  useEffect(() => {
    if (roadmapData) {
      const transformedData = transformApiToFrontend(roadmapData);
      setRoadmap(transformedData);
    }
  }, [roadmapData]);

  // 에러 메시지 변환 (한국어)
  const error = queryError 
    ? (queryError as any)?.response?.data?.message || 
      (queryError as any)?.message || 
      '로드맵 정보를 불러오는 중 오류가 발생했습니다.'
    : null;

  // Orval Mutation 훅 사용
  const subscribeMutation = useSubscribeRoadmap();
  const unsubscribeMutation = useUnsubscribeRoadmap();
  const [isSubscribing, setIsSubscribing] = useState(false);

  /**
   * 구독/구독취소 토글 처리
   */
  const handleSubscribeToggle = useCallback(async () => {
    if (!roadmapId || !roadmap?.summary?.id) return;

    const currentSubscribedState = roadmap.summary.isSubscribed;
    setIsSubscribing(true);

    try {
      if (currentSubscribedState) {
        await unsubscribeMutation.mutateAsync({ roadmapId });
      } else {
        await subscribeMutation.mutateAsync({ roadmapId });
      }

      // Optimistic update - 즉시 UI 반영
      setRoadmap(prev => prev ? ({
        ...prev,
        summary: {
          ...prev.summary,
          isSubscribed: !currentSubscribedState,
          subscriptionCount: currentSubscribedState
            ? (prev.summary.subscriptionCount ?? 1) - 1
            : (prev.summary.subscriptionCount ?? 0) + 1,
        }
      }) : null);

    } catch (err) {
      console.error(`구독 ${currentSubscribedState ? '취소' : ''} 실패:`, err);
      // 에러 시 데이터 다시 가져오기
      refetch();
    } finally {
      setIsSubscribing(false);
    }
  }, [roadmapId, roadmap, subscribeMutation, unsubscribeMutation, refetch]);

  // 삭제 Mutation 훅
  const deleteMutation = useDeleteRoadmap();
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * 로드맵 삭제 처리
   */
  const handleDeleteRoadmap = useCallback(async (): Promise<boolean> => {
    if (!roadmapId || !roadmap?.summary?.id) return false;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm("정말로 이 로드맵을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
      return false;
    }

    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync({ roadmapId });
      console.log(`로드맵 ${roadmap.summary.id} 삭제 완료`);
      return true;
    } catch (err) {
      console.error('로드맵 삭제 실패:', err);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [roadmapId, roadmap, deleteMutation]);

  return {
    roadmap,
    isLoading,
    error,
    isSubscribing,
    isDeleting,
    loadRoadmapDetail: refetch,
    handleSubscribeToggle,
    handleDeleteRoadmap,
  };
};

export type { RoadmapDetail };
